import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog, faFilter, faLongArrowAltDown, faLongArrowAltUp, faSort, faSync } 
  from "@fortawesome/free-solid-svg-icons";
import { ApplicationState } from "../../../../../store";
import { connect, useDispatch, useSelector } from "react-redux";
import { Grade } from "../../../../../store/groups/types";
import {
  DataPeriod,
  EvidencePeriodData,
  GradeLevels,
  MasterDataClasses,
  MasterDataTeachers,
  School,
  TeacherClass,
  TeacherInfo,
  genderList,
} from "../../../../../store/onboarding/types";
import { Dropdown, Form, Modal, Overlay, OverlayTrigger, Popover, Spinner, Table, Tooltip } from "react-bootstrap";
import StudentsFilter from "./student-data-table/filters/StudentsFilter";
import TeachersFilter from "./student-data-table/filters/TeachersFilter";
import ClassesFilter from "./student-data-table/filters/ClassesFilter";
import SchoolsFilter from "./student-data-table/filters/SchoolsFilter";
import GradeFilter from "./student-data-table/filters/GradeFilter";
import CoachDataPeriodSelector from "../../../../pages/coach-dashboard/CoachDataPeriodSelector";
import DataPeriodsPanel from "../../../../pages/coach-dashboard/tabs/your-data-and-interviews/DataPeriodsPanel";
import { getEvidenceInDataPeriod, hidePreClassWideNeedsModal, openClassWideNeedsModal, openPreClassWideNeedsModal, resetEvidenceInDataPeriod } from "../../../../../store/onboarding/actions";
import SortDataModal from "./SortDataModal";
import FontAwesomeIconDropDown from "./student-data-table/drop-down/FontAwesomeIconDropDown";
import { Portal } from "react-portal";
import CustomMenuItem from "./student-data-table/drop-down/CustomMenuItem";
import { getDisplayedLabel } from "./student-data-table/cells/AssessmentLabelTooltip";
import { evidenceColumnGroupDisplayTitle } from "./student-data-table/Helpers";
import { dataFilterApplied, resetFilterAction, resetPagination, resetSortAction } from "../../../../../store/dataFilter/actions";
import { DataSortState } from "../../../../../store/dataSort/types";
import { showConfirmDialog } from "../../../../../store/confirm-dialog/actions";
import DemographicsFilter from "./student-data-table/filters/DemographicsFilter";
import usePrevProps from "../../../../../utils/hooks/usePrevProps";


type StateProps = {
  schools: Array<School>;
  teachersClasses: Array<TeacherClass>;
  teacherRoster: Array<TeacherInfo>;
  teacherIdFilter?: number[];
  schoolIdsFilter?: number[];
  gradeLevelFilter?: Grade[];
  classFilter?: number[];
  studentsFilter?: string;
  showPreClassWideNeedsModal?: boolean;
  activeSort?: DataSortState[];
  raceFilter: string[];
  raceOtherFilter: string[];
  genderFilter: string[];
  specialEducationFilter: boolean | undefined;
  ellFilter: boolean | undefined;
  sldFilter: boolean | undefined;
  freeOrReducedLunchFilter: boolean | undefined;
  currentDataPeriod?: DataPeriod;
};

const ASSESSMENT_DISPLAYED_CHARACTERS_NUM = 20;

type OwnProps = {
  isReadonly?: boolean;
  evidenceGroupData: any;
  numberDisplayedGroups: number;
  onDisplayedGroupsChange: (
    evidenceColumnGroupId: number,
    dataPeriodId: number
  ) => any;
  checkDisplayedGroups: (
    evidenceColumnGroupId: number,
    dataPeriodId: number
  ) => boolean;
  isDataPeriodLoading?: boolean;
};

type Props = StateProps & OwnProps;

const DataFiltersPanel: FunctionComponent<Props> = (props) => {

  let {
    evidenceGroupData,
    numberDisplayedGroups,
    checkDisplayedGroups,
    onDisplayedGroupsChange,
    showPreClassWideNeedsModal,
  } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [filterApplied, setFilterApplied] = useState<boolean>(false);
  const [showAssessmentDropDown, setShowAssessmentDropDown] = useState(false);
  const partialGroupDisplay = useMemo(() => {
    return evidenceGroupData?.evidenceColumnGroup?.length > 1 &&
    evidenceGroupData?.evidenceColumnGroup?.length !== numberDisplayedGroups; 
  }, [numberDisplayedGroups]) 
 
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);

  const activeSort = useSelector(
    (s: ApplicationState) => s.dataSort.activeSort
  );

  const prevProps = usePrevProps({
    currentDataPeriod: props.currentDataPeriod,
  });

  useEffect(() => {
    if(prevProps?.currentDataPeriod && (prevProps?.currentDataPeriod.id !== props.currentDataPeriod?.id)) {
      dispatch(resetFilterAction());
      dispatch(resetPagination());
      dispatch(resetSortAction());
      setFilterApplied(false);
    }
  },[props.currentDataPeriod?.id])

  const multiSortIndication = useMemo(() => {
    const sortOptions = activeSort;
    const sortOrder: any = [];
    if (sortOptions.length) {
      for (let i in sortOptions) {
        sortOrder.push({ columnName: sortOptions[i].col.measurement.column_name, order: sortOptions[i].direction });
      }
    }
    return sortOrder
  },[activeSort])


  useEffect(() => {
    setLoading(false);
  },[showPreClassWideNeedsModal])

  const evidencePeriodData = useSelector<
    ApplicationState,
    EvidencePeriodData>((s) => s.onboarding.evidencePeriodData!);

  const classesDataInFilter = useSelector<
    ApplicationState,
    MasterDataClasses[]
  >((s) => s.onboarding.masterDataInFilter.classes);

  const teachersDataInFilter = useSelector<
      ApplicationState,
      MasterDataTeachers[]
    >((s) => s.onboarding.masterDataInFilter.teachers);

  const teachersByCurrentDataPeriod: any[] = useMemo(() => 
      teachersDataInFilter.filter((teacher) => teacher.teacher_id !== null)
    ,[teachersDataInFilter])
  
  const generalFilter = useMemo(() => {
    const messageParts = [];
    if (props.studentsFilter?.length) {
      messageParts.push(`Student - ${props.studentsFilter}`)
    }

    if (props.teacherIdFilter?.length) {
      const teachers = teachersByCurrentDataPeriod.filter(
        (t: MasterDataTeachers) => props.teacherIdFilter?.some((teacherId) =>  t.teacher_id === teacherId) 
      );
      if (teachers.length) {
        let teachersName: string[] = teachers?.map((teacher) => `${teacher.first_name} ${teacher.last_name}`)
        const more  = props.teacherIdFilter.length > 1 
        ? ' and '+ (props.teacherIdFilter.length - 1)+ '+'
        : ''
        
        messageParts.push(`Educator(s) - ${teachersName.length ? teachersName[0] + more : ''}`)
      }
    }

    if (props.classFilter?.length) {
      const tClasses: any[] = props.teachersClasses.filter(
        (tc) => props.classFilter?.includes(tc.id!)
      ).map((tClass) => tClass.name);
      if (tClasses.length) {
        const more  = props.classFilter.length > 1 
        ? ' and '+ (props.classFilter.length - 1)+ '+'
        : ''
        messageParts.push(`Class(es) - ${tClasses.length ? tClasses[0] + more : ''}`)
      }
    }

    if (props.schoolIdsFilter?.length) {
      let schoolNames = props.schoolIdsFilter
      .map((id) => props.schools.find((school) => school.id === id)?.name);
      const more  = props.schoolIdsFilter.length > 1 
      ? ' and '+ (props.schoolIdsFilter.length - 1)+ '+'
      : ''
      messageParts.push(`School(s) - ${schoolNames.length ? schoolNames[0] + more : ''}`)
    }

    if (props.gradeLevelFilter?.length) {
      const more  = props.gradeLevelFilter.length > 1 
      ? ' and '+ (props.gradeLevelFilter.length - 1)+ '+'
      : ''
      messageParts.push(`Grade Level(s) - ${GradeLevels.find((gl) => gl.value == props.gradeLevelFilter![0])?.label + more}`)
    }

    return messageParts;
  }, [
    evidencePeriodData,
    filterApplied
  ]);

  const demoGraphicsFilter = useMemo(() => {
    const messageParts = [];
    let raceData: any[] = [];
    if(props.raceFilter?.length) {
      raceData = [...raceData, ...props.raceFilter];
    } else if(props.raceOtherFilter?.length) {
      raceData = [...raceData, ...props.raceOtherFilter];
    } 
    if(raceData.length) {
      const more  = raceData?.length > 1 
        ? ' and '+ (raceData?.length - 1)+ '+'
        : ''
      messageParts.push(`Race(s) - ${raceData.length ? raceData[0] + more : '' }`)
    }
    if(props.genderFilter?.length) {
      const more  = props.genderFilter?.length > 1 
        ? ' and '+ (props.genderFilter?.length - 1)+ '+'
        : ''
      let genderArray = genderList.filter((gender) => props.genderFilter.includes(gender.value))
      messageParts.push(`Gender(s) - ${genderArray.length ? genderArray[0]?.label + more : ''}`) 
    }
    if(props.specialEducationFilter != undefined) {
      messageParts.push(`Special Education - ${props.specialEducationFilter ? 'Y' : 'N' }`)
    }
    if(props.freeOrReducedLunchFilter != undefined) {
      messageParts.push(`Free Or Reduced Lunch - ${props.freeOrReducedLunchFilter ? 'Y' : 'N' }`)
    }
    if(props.ellFilter != undefined) {
      messageParts.push(`ELL - ${props.ellFilter ? 'Y' : 'N' }`)
    }
    if(props.sldFilter != undefined) {
      messageParts.push(`SLD - ${props.sldFilter ? 'Y' : 'N' }`)
    }

    return messageParts;
  }, [
    evidencePeriodData,
    filterApplied
  ]);

  let valueExitInFilter = useMemo(() => {
    return  props.studentsFilter?.trim() ||
            props.teacherIdFilter?.length ||
            props.gradeLevelFilter?.length ||
            props.classFilter?.length ||
            props.schoolIdsFilter?.length ||
            props.raceFilter?.length ||
            props.raceOtherFilter?.length ||
            props.genderFilter?.length ||
            (props.specialEducationFilter != undefined) ||
            (props.ellFilter != undefined) ||
            (props.sldFilter  != undefined) ||
            (props.freeOrReducedLunchFilter != undefined)
          ? true : false
  }, [
    props.studentsFilter,
    props.teacherIdFilter,
    props.gradeLevelFilter,
    props.classFilter,
    props.schoolIdsFilter,
    props.raceFilter,
    props.raceOtherFilter,
    props.genderFilter,
    props.specialEducationFilter,
    props.ellFilter,
    props.sldFilter,
    props.freeOrReducedLunchFilter,
  ])

  const [showFiltersOverlay, setShowFiltersOverlay] = useState<boolean>(false);
  const [showMultiSortOverlay, setShowMultiSortOverlay] = useState<boolean>(false);

  const filtersBtnRef = useRef(null);

  const handleFiltersClick = () => {
    setShowFiltersOverlay((prevState) => !prevState);
  };

  const onHideFiltersOverlay = () => {
    setShowFiltersOverlay(false);
  };

  const multiSortBtnRef = useRef(null);

  const handleMultiSortClick = () => {
    setShowMultiSortOverlay((prevState) => !prevState);
  };

  const onHideMultiSortOverlay = () => {
    setShowMultiSortOverlay(false);
  };

  const noClassWideNeeds = useSelector<ApplicationState, boolean | undefined>(
    (s) => s.onboarding.noClassWideNeeds
  );

  //console.log(noClassWideNeeds , (!props.isDataPeriodLoading && !evidenceGroupData?.evidenceColumnGroup.length))

  // const rowsFilter = (studentRow: StudentRow) => {
  //   let result: boolean = true;

  //   if (props.teacherIdFilter) {
  //     result =
  //       result &&
  //       !!studentRow.student.teachers?.some(
  //         (teacher) => props.teacherIdFilter?.includes(teacher.id!)
  //       );
  //   }

  //   if (props.studentsFilter) {
  //     result =
  //       result &&
  //       getFullName(studentRow.student)
  //         .toLowerCase()
  //         .includes(props.studentsFilter.toLowerCase());
  //   }

  //   if (props.schoolIdsFilter && props.schoolIdsFilter.length > 0) {
  //     result =
  //       result &&
  //       studentRow.student
  //         .teachers!.flatMap((teacher) => teacher.schools || [])
  //         .some((schoolId) => schoolId && props.schoolIdsFilter?.includes(schoolId));
  //   }

  //   // if (teacherIdFilter && teacherIdFilter.length > 0) {
  //   //   result =
  //   //     result &&
  //   //     studentRow.student.teachers!.some(
  //   //       (teacher) => teacher.id && teacherIdFilter.includes(teacher.id)
  //   //     );
  //   // }
  //   if (props.classFilter?.length) {
  //     result =
  //       result &&
  //       studentRow.student.teachers!.some((st) => st.classes?.find((cls) => props.classFilter?.includes(cls.id)));
  //   }
  //   if (evidenceGroupData && props.gradeLevelFilter && props.gradeLevelFilter.length > 0) {
  //     result =
  //       result &&
  //       evidenceGroupData.evidenceColumnGroup.some((ecg: EvidenceColumnGroup) =>
  //         ecg.evidence_columns.some((ec) =>
  //           ec.student_entries.some(
  //             (se) =>
  //               se.student_id === studentRow.student.id &&
  //               se.grade &&
  //               props.gradeLevelFilter?.includes(se.grade)
  //           )
  //         )
  //       );
  //   }
  //   return result;
  // };

  const handleHide = () => {
    setLoading(false);
    dispatch(hidePreClassWideNeedsModal());
  };

  const handleResetSort = () => {
    dispatch(
      showConfirmDialog({
       title: "Clear All Sorting",
        text: (
          <>
            <p className="text-center font-weight-bold">Are you sure you want to clear all sorting?</p>
            <p className="text-center">This will remove all applied sorting and restore the default order.</p>
          </>
        ),
        onConfirm: () => {
          dispatch(resetSortAction());
        },
        confirmButtonText: "CLEAR ALL",
      })
    );
  }

  const handleResetFilters = () => {
    dispatch(
      showConfirmDialog({
        title: "Clear All Filter(s)",
        text: (
          <>
            <p className="text-center font-weight-bold">Are you sure you want to clear all filter(s)?</p>
            <p className="text-center">This will remove all applied filters and show all data.</p>
          </>
        ),
        onConfirm: () => {
          dispatch(resetFilterAction());
          dispatch(resetPagination());
          setFilterApplied(false);
          dispatch(getEvidenceInDataPeriod(props.currentDataPeriod?.id));
        },
        confirmButtonText: "CLEAR ALL",
      })
    );
  }

  const applyFilter = () => {
      setFilterApplied(true);
      dispatch(dataFilterApplied(true))
      dispatch(resetPagination());
      dispatch(resetSortAction());
      dispatch(getEvidenceInDataPeriod(props.currentDataPeriod?.id));
      // if(!filterDataExist) {
      //   setTimeout(() => {
      //     document.body.click();
      //   }, 100);
      //   props.sendDataFilterMessage([]);
      // }
      return Promise.resolve(1);
    }
  
   return (
    <div id={"evidence-table-filter-row"} className="filterRow">
      <div className={`${!evidenceGroupData?.evidenceColumnGroup.length ? 
      "emptyDataPeriod" : 'filterRowDataPeriod'} ${props.isDataPeriodLoading ? 'loadingDataPeriod' : ''}`}>
        <div>Viewing data for:</div>
        <div>
          <CoachDataPeriodSelector small />
          <DataPeriodsPanel />
        </div>
      </div>

      
      {evidenceGroupData?.evidenceColumnGroup.length > 0 ?
        <>
          <div className="row flex-column justify-content-center">
            <div className="col-auto header-content">
              {/* assessement table dropdown code */}
              
                <OverlayTrigger
                  overlay={<Tooltip id="editIcon">Click to show/hide tables</Tooltip>}
                >
                      <div className="d-flex align-items-center">
                          <div className="assessmentLabelDetails pointer">
                            <div className="assessmentLabelName" onClick={() => setShowAssessmentDropDown(!showAssessmentDropDown)}>
                              <div>
                                <span className="mr-1">
                                  <FontAwesomeIcon icon={faCog} />
                                </span>
                                {getDisplayedLabel(
                                  `Showing ${partialGroupDisplay ? 'Partial': 'All'} Data`,
                                  ASSESSMENT_DISPLAYED_CHARACTERS_NUM
                                )}
                              </div>
                              {partialGroupDisplay ? (
                                <div className="partialColumnsDisplay">
                                  (Displaying {numberDisplayedGroups} of{" "}
                                  {evidenceGroupData.evidenceColumnGroup.length} tables)
                                </div>
                              ) : ''}
                            </div>
                          </div>
      
                        </div>
                </OverlayTrigger>
            
              {/* assessement table dropdown code */}
            </div>
            <div className="d-flex justify-content-between">
              <div className="col-auto pl-0 d-flex align-items-center">
                 <OverlayTrigger
                  overlay={<Tooltip id="multisortIcon">Sorting is based on the data rows currently displayed. Load more rows if needed.</Tooltip>}
                >
                  <span onClick={handleMultiSortClick} className="pointer">
                    <span ref={multiSortBtnRef}>
                    {`Multi-Sort `}</span> {" "}
                    {multiSortIndication.length ?
                      <OverlayTrigger
                        placement="bottom"
                        overlay={
                          <Popover id="showMultiSort">
                              <Popover.Title>Sort By Column with Order</Popover.Title>
                              <Popover.Content>
                                <Table className="table-bordered table-sm">
                                  <tbody>
                                    {multiSortIndication.map((multi: any) => 
                                    <tr>
                                      <td className="testLabel" style={{background:"#00c6f7"}}>
                                        <h5 style={{color:"#00265b", fontWeight: 600}}>
                                          <FontAwesomeIcon
                                            icon={
                                              multi.order == 'Descending' ? faLongArrowAltUp : faLongArrowAltDown
                                            }
                                            style={{color:"#fff"}}
                                            className="mr-1"
                                          /> {" "} {multi.columnName}
                                        </h5>
                    
                                      </td>
                                    </tr>)}
                                  </tbody>
                                </Table>
                              </Popover.Content>
                            </Popover>
                        }
                      >
                        <span className="shortingBtnSmFilter marigoldBtnSm ml-1"><FontAwesomeIcon icon={faSort} /></span>
                      </OverlayTrigger>
                    :
                      <span><FontAwesomeIcon icon={faSort} /></span>
                    }
                  </span>

                </OverlayTrigger>
              {props.activeSort?.length ? 
                <span className="redBtnSmFilter ml-1 " title="Clear All Sorts" onClick={handleResetSort}>
                  <FontAwesomeIcon 
                    icon={faSync}  
                    color="red"
                    className="pointer"
                    style={{verticalAlign: "middle"}}
                  />
                </span>
              : ''}
              </div>
              <div className="col-auto pr-0 d-flex align-items-center" style={{maxWidth: '280px'}}>
                <div onClick={handleFiltersClick} className="pointer">
                  <span ref={filtersBtnRef}>{" Filter "}</span>{" "}
                  {(generalFilter.length || demoGraphicsFilter.length) && filterApplied ?
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Popover id="showFilters" className="showFilterClass">
                          <Popover.Title>Applied Filter(s)</Popover.Title>
                          <Popover.Content>
                          <div className="d-flex">
                            {generalFilter.length ? 
                              <Table className="table-bordered table-sm">
                                <tbody>
                                  {generalFilter.map((generalFilterData: any) => 
                                    <tr>
                                      <td style={{background:"#00c6f7"}}>
                                        <h5 style={{color:"#00265b", fontWeight: 600}}>{generalFilterData}</h5>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </Table>
                            : ''}
                            {demoGraphicsFilter.length
                            ?
                              <Table className="table-bordered table-sm">
                                <tbody>
                                  {demoGraphicsFilter.map((demoGraphicsFilterData: any) => 
                                    <tr>
                                      <td style={{background:"#00c6f7"}}>
                                        <h5 style={{color:"#00265b", fontWeight: 600}}>{demoGraphicsFilterData}</h5>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </Table>
                            : ""}
                            </div>
                          </Popover.Content>
                        </Popover>
                    }
                  >
                    <span className="shortingBtnSmFilter marigoldBtnSmFilter ml-1 pointer"><FontAwesomeIcon icon={faFilter}/></span>
                  </OverlayTrigger>
                  :
                  <span><FontAwesomeIcon icon={faFilter}/></span>
                  }
                </div>
              {(generalFilter.length || demoGraphicsFilter.length) && filterApplied ? 
                <span className="redBtnSmFilter ml-1" title="Clear Filter(s)" onClick={handleResetFilters}>
                  <FontAwesomeIcon 
                    icon={faSync}  
                    color="red"
                    size="1x"
                    className="pointer"
                    style={{verticalAlign: "middle"}}
                  />
                </span>
              : ''}
              </div>
            </div>
          </div>
          <div className="px-3">
            <button
              disabled={!!noClassWideNeeds || (!props.isDataPeriodLoading && !evidenceGroupData?.evidenceColumnGroup.length)}
              className={!!noClassWideNeeds || (!props.isDataPeriodLoading && !evidenceGroupData?.evidenceColumnGroup.length) ? "whiteBtnSm" : "marigoldBtnSm"}
              onClick={() => !props.isDataPeriodLoading && evidenceGroupData?.pagination.totalRowCount > 500 ?
                dispatch(openPreClassWideNeedsModal()): dispatch(openClassWideNeedsModal()) }
            >
              {!!noClassWideNeeds || (!props.isDataPeriodLoading && !evidenceGroupData?.evidenceColumnGroup.length) ? "No Class-Wide Needs" : "Show Class-Wide Needs"}
            </button>
          </div>
          <Overlay
            placement="bottom"
            target={filtersBtnRef?.current as any}
            show={showFiltersOverlay}
            rootClose
            rootCloseEvent={"click"}
            onHide={onHideFiltersOverlay}
          >
            <Popover id="student-data-table-filters" className="popOverClass">
              <Popover.Content>
                <div className="row">
                  <div className="col-6">
                    <div className="studentDataTableFilters">
                      <StudentsFilter />
                      <TeachersFilter />
                      <ClassesFilter />
                      <SchoolsFilter />
                      <GradeFilter />
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="studentDataTableFilters">
                      <DemographicsFilter fromDataFilter/>
                    </div>
                  </div>
                  <div className="col-12 mt-1 text-center">
                    <button className="blueBtnSm" disabled={!valueExitInFilter} onClick={() => applyFilter().then(() => document.body.click())}>Apply</button>
                  </div>
                </div>
              </Popover.Content>
            </Popover>
          </Overlay>
          <Overlay
            placement="bottom"
            target={multiSortBtnRef?.current as any}
            show={showMultiSortOverlay}
            rootClose
            rootCloseEvent={"click"}
            onHide={onHideMultiSortOverlay}
          >
            <Popover id="student-data-table-multi-sort" className="multishortClass">
              <Popover.Content>
                <SortDataModal show={showMultiSortOverlay} setShow={setShowMultiSortOverlay}/>
              </Popover.Content>
            </Popover>
          </Overlay>
          {' '}
          {/* { show && <SortDataModal show={show} setShow={setShow}/>} */}
          {showPreClassWideNeedsModal ? 
            <Modal
            show={showPreClassWideNeedsModal}
            onHide={handleHide}
            animation={false}
            backdrop="static"
            keyboard={false}
            backdropClassName="customDarkModalBackdrop in"
            centered
          >
            <Modal.Header closeButton className="purpleModalHeader">
              <Modal.Title>Generate Class-wide Needs</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="text-center pb-3">
                <p>
                  Class-wide needs support up to 3,000 students and may take additional time to generate for large datasets.
                </p>
                <div>
                  <span className="alert alert-warning p-1">
                    <strong>Note:</strong> Results are generated based on applied filters, if any.
                  </span>
                </div>
              </div>
              <hr/>
              <div className="btnActions">
                  <button className="whiteBtnSm" onClick={() => handleHide()}>
                      Cancel
                  </button>
                  <button className="blueBtnSm" onClick={() => {
                    setLoading(true);
                    setTimeout(() => {
                      dispatch(openClassWideNeedsModal());
                    }, 10);
                  }}>
                    {"Continue"}
                    {loading && <Spinner className="ml-1" animation="border" size="sm" />}
                  </button>
              </div>
            </Modal.Body>
          </Modal>
          :''
          }    
          <Modal
            show={showAssessmentDropDown}
            onHide={() => setShowAssessmentDropDown(false)}
            animation={false}
            backdropClassName="customDarkModalBackdrop in"
            centered
          >
            <Modal.Header
              closeButton
              className={"purpleModalHeader"}
            >
              <Modal.Title>Show/Hide Tables</Modal.Title>
            </Modal.Header>
            <Modal.Body>
            {(evidenceGroupData != undefined) 
              ?
                <>
                  <label>Check or uncheck to show or hide the tables.</label>
                  <hr/>
                  {evidenceGroupData?.evidenceColumnGroup?.map(
                      (evidenceGroup: any, index: number) => (
                        <Dropdown.Item key={index} as={CustomMenuItem}>
                          <div className="d-flex align-items-center">
                            <Form.Check
                              readOnly
                              custom
                              id={`evidence-group-${evidenceGroup.id}`}
                              className="evidenceGroupItemCheckbox"
                              type="checkbox"
                              checked={checkDisplayedGroups(
                                evidenceGroupData.dataPeriodId,
                                evidenceGroup.id
                              )} 
                              onClick={onDisplayedGroupsChange(
                                evidenceGroupData.dataPeriodId,
                                evidenceGroup.id
                              )} />
                            <label className="pointer" htmlFor={`evidence-group-${evidenceGroup.id}`}>{evidenceColumnGroupDisplayTitle(evidenceGroup)}</label>
                          </div>
                        </Dropdown.Item>
                    )
                  )} 
                </>
              : ''}
            </Modal.Body>
          </Modal>
        </>
      : " "}


    </div>
  );
};

const mapStateToProps = ({
  onboarding,
  dataFilter,
  dataSort
}: ApplicationState): StateProps => {
  return {
    schools: onboarding.schools,
    teachersClasses: onboarding.teachersClasses,
    teacherRoster: onboarding.teachersRoster,
    teacherIdFilter: onboarding.teacherIdFilter,
    schoolIdsFilter: dataFilter.dataSchoolsFilter,
    gradeLevelFilter: dataFilter.dataGradesFilter,
    classFilter: dataFilter.classIdFilter,
    studentsFilter: onboarding.studentsFilter,
    showPreClassWideNeedsModal: onboarding.modalsState.showPreClassWideNeedsModal,
    activeSort: dataSort?.activeSort,
    raceFilter: dataFilter.raceFilter,
    raceOtherFilter: dataFilter.raceOtherFilter,
    genderFilter: dataFilter.genderFilter,
    specialEducationFilter: dataFilter.specialEducationFilter,
    ellFilter: dataFilter.ellFilter,
    sldFilter: dataFilter.sldFilter,
    freeOrReducedLunchFilter: dataFilter.freeOrReducedLunchFilter,
    currentDataPeriod: onboarding.currentDataPeriod,
  };
};

export default connect(mapStateToProps)(DataFiltersPanel);